import { Icon } from '@iconify/react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Link,
  Paper,
} from '@mui/material';
import React, { useEffect, useMemo, useState, useImperativeHandle } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { addDays, format, isValid } from 'date-fns';
import { bignumber } from 'mathjs';
import {
  changeInvoiceField,
  getInvoiceBreakdown,
  invoiceForm,
  invoiceGLItemsForm,
  getInvoiceViewers,
  getFacilityInvoicingApprovers,
  saveApprovers,
  fetchDuplicateInvoices,
} from '../../../../redux/slices/invoicing';
import { useDispatch, useSelector } from '../../../../redux/store';
import FieldMapping from '../../../../reusable-components/formFieldPairs/FieldMapping';
import { FieldMappingProvider } from '../../../../reusable-components/formFieldPairs/FieldMappingContext';
import { bobAIIcon } from '../../../../reusable-components/bobAI/bobAI';
import Iconify from '../../../../reusable-components/iconify';
import usePrompt from '../../../../reusable-components/prompt/usePrompt';
import Scrollbar from '../../../../reusable-components/scrollbar';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { fCurrency } from '../../../../utils/formatNumber';
import { fMonthDayYearShort, parseDateOnlyToLocalDate } from '../../../../utils/formatTime';
import { InvoiceParsedField } from '../../../../utils/globalEnums';
import { DuplicateInvoiceAlert, RelatedInvoiceAlert, DuplicateInvoicePopup } from './RelatedInvoices';
import { useTabs } from '../../../../context/TabContext';
import { useIsInvoiceEditable } from './reusable/hooks/useInvoiceLocks';
import { useWithDuplicateCheck } from './reusable/hooks/useWithDuplicateCheck';
import { useVendorFields } from './reusable/hooks/useVendorFields';

const InvoiceForm = React.forwardRef(
  (
    {
      setHighlightedGls,
      glRows,
      setOpenGlCodeSummary,
      setInvoiceFacilityId,
      unsavedGlData,
      setUnsavedGlData,
      highlightItem,
      invoiceDataLoaded,
      selectedApprovers,
      setSelectedApprovers,
      setHighlightedRelatedInvoices,
      onBobAIHoverStart,
      onBobAIHoverEnd,
    },
    ref
  ) => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const { withDuplicateCheck, showDuplicatePopup, ...formPopupProps } = useWithDuplicateCheck();
    const {
      data: {
        invoiceData,
        invoiceItemData,
        invoiceGLItems,
        facilityInvoicingApprovers,
        invoiceApprovers,
        relatedInvoices: { invoiceId: relatedInvoiceId, related = [] },
        duplicateInvoices: { invoiceId: duplicateInvoiceId, duplicates = [] },
      },
    } = useSelector((state) => state.invoicing);
    const { invoice } = invoiceItemData || {};
    const parsedFields = invoiceItemData?.invoice?.parsedFields || 0;   
    const { vendors: autoVendors, manualVendors } = invoiceData;
    const manualInvoice = invoice?.sourceMethod === 'Manual';
    const { control, setValue, handleSubmit, getValues, watch, trigger } = useForm({
      mode: 'onChange',
      defaultValues: invoice,
    });
    const { enqueueSnackbar } = useSnackbar();
    const { isEditable: hasEditAccess } = useIsInvoiceEditable();
    const navigate = useNavigate();
    const [editInvoice, setEditInvoice] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [expand, setExpand] = useState(false);
    const [unsavedData, setUnsavedData] = useState(null);
    const facId = watch('facilityId') || 0;
    const vendorId = watch('vendorId') || 0;
    const vendorInvoiceId = watch('vendorInvoiceId') || '';
    const totalSum = invoiceItemData?.invoiceItems?.reduce((accumulator, item) => {
      const itemTotal = bignumber(Number(item?.price))
        .times(bignumber(Number(item?.quantity)))
        .plus(bignumber(Number(item?.taxAmount)));
      // Round each line to cents, with half cents rounded away from zero.
      return accumulator.plus(itemTotal.toDecimalPlaces(2, 4));
    }, bignumber(0));
    const calculatedTotal =
      invoiceGLItems.length > 0
        ? invoiceGLItems
          .map((i) => Number(i.amount) + Number(i.shipping))
          ?.reduce((accumulator, current) => accumulator + current, 0) ?? 0
        : bignumber(Number(invoiceItemData?.invoice?.shipping))
            .plus(totalSum ?? NaN)
            .toDecimalPlaces(2, 4)
            .toNumber();
    const calculatedShippingTotal =
      invoiceGLItems.length > 0
        ? invoiceGLItems.map((i) => Number(i.shipping))?.reduce((accumulator, current) => accumulator + current, 0) ?? 0
        : bignumber(Number(invoiceItemData?.invoice?.shipping))
          .toDecimalPlaces(2, 4)
          .toNumber();
    const facilities = useMemo(
      () => (invoice?.sourceMethod === 'Manual' ? invoiceData.manualFacilities : invoiceData.facilities),
      [invoice?.sourceMethod, invoiceData.manualFacilities, invoiceData.facilities]
    );
    const bookingTypeId = useMemo(() => facilities?.find((f) => f.value === facId)?.bookingTypeId, [facilities, facId]);
    const { updateVendorValue, vendorOptions, vendorTooltipText, canAddVendor, selectedVendor } = useVendorFields({
      setValue,
      invoiceItemData,
      autoVendors,
      manualVendors,
      setUnsavedData,
      bookingTypeId,
    });
    const date = useWatch({ control, name: 'date' });
    const { addTab } = useTabs();

    const openOrderInNewTab = () => {
      addTab();
      navigate(`/dashboard/orders/${invoiceItemData?.invoice?.orderId}`);
    };
    useEffect(() => {
      if (date) {
        setValue('glPostingDate', date);
      }
    }, [date, setValue]);
    useEffect(() => {
      dispatch(getFacilityInvoicingApprovers(invoice?.sourceMethod === 'Manual'));
    }, [dispatch]);

    useEffect(() => {
      setSelectedApprovers(
        invoiceApprovers?.map((approver) => ({
          approvalIndex: approver.approvalIndex,
          approverEmail: approver.approverEmail,
          approverFullName: approver.approverFullName,
          status: approver.status,
          id: approver.id,
        }))
      );
    }, [invoiceApprovers]);

    const debouncedFetchDuplicates = useMemo(
      () =>
        debounce((vendorInvoiceId, facId, vendorId) => {
          dispatch(
            fetchDuplicateInvoices({
              Id: invoice.id,
              VendorInvoiceId: vendorInvoiceId,
              VendorId: vendorId,
              FacilityId: facId,
              MultiFacility: getValues('multiFacility'),
              SourceMethod: invoice?.sourceMethod,
            })
          );
        }, 800),
      [dispatch, invoice?.id]
    );

    useEffect(() => {
      if (!invoice || invoice?.id !== Number(id)) return;
      debouncedFetchDuplicates(vendorInvoiceId, facId, vendorId);
    }, [vendorInvoiceId, facId, vendorId, id, invoice?.id, invoiceItemData]);

    usePrompt(editInvoice);

    useImperativeHandle(ref, () => ({
      validateAndSave: async () => {
        const isValid = await trigger(); // Validate all fields
        if (isValid) {
          const invoiceEdited = isInvoiceEdited();
          if (!invoiceEdited) return true;
          const response = await dispatch(invoiceForm({ invoice: getValues() }));
          if (isFacilityFieldEdited) getInvoiceViewers(invoice.id);

          enqueueSnackbar(response.message ? response.message : 'Error updating invoice.', {
            variant: response.success ? 'success' : 'error',
            persist: response.keepFormOpen,
          });
          if (response.success) {
            return true;
          }
          return false;
        }
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
        return false; // Inform the parent of validation failure
      },
    }));

    const initializeData = async () => {
      if (invoiceItemData?.invoice !== null && invoiceItemData?.invoice !== undefined) {
        Object.entries(invoiceItemData?.invoice).forEach(([key, value]) => {
          if ((key === 'facilityId' || key === 'vendorId') && value === 0) {
            setValue(key, '');
          } else if (key === 'invoiceType' && value !== 'Invoice' && value !== 'CreditMemo') {
            setValue('invoiceType', invoiceItemData?.invoice?.total >= 0 ? 'Invoice' : 'CreditMemo');
          } else setValue(key, value ?? '');
        });

        setDataLoaded(true);
      }
    };
    const saveUnsavedGls = async () => {
      if (unsavedGlData === null || Object.values(unsavedGlData)?.length === 0) {
        await dispatch(getInvoiceBreakdown(id));

        return;
      }
      const glItemsValues = Object.values(unsavedGlData);
      const response = await dispatch(invoiceGLItemsForm({ invoiceGlItems: glItemsValues }));
      enqueueSnackbar(response.message ? response.message : 'Error updating GL Items.', {
        variant: response.success ? 'success' : 'error',
        persist: response.keepFormOpen,
      });
      if (response.success) {
        setUnsavedGlData(null);
        const newShipping = glItemsValues.reduce(
          (accumulator, currentValue) => parseFloat(accumulator) + parseFloat(currentValue.shipping),
          0
        );
        const total =
          glItemsValues
            ?.map((i) => Number(i.amount) + Number(i.shipping))
            ?.reduce((accumulator, current) => accumulator + current, 0) ?? 0;
        if (invoice?.sourceMethod === 'Manual' && invoiceItemData?.invoiceItems.length === 0) {
          await dispatch(changeInvoiceField(id, { key: 'Total', value: total }));
          await dispatch(changeInvoiceField(id, { key: 'Shipping', value: newShipping }));
        }

        if (total > 0) await dispatch(changeInvoiceField(id, { key: 'InvoiceType', value: 'Invoice' }));
        else if (total < 0) await dispatch(changeInvoiceField(id, { key: 'InvoiceType', value: 'CreditMemo' }));
      }
      await dispatch(getInvoiceBreakdown(id));
    };

    //set Default Approvers For Facility
    useEffect(() => {
      const approversForFacility = facilityInvoicingApprovers?.filter(
        (x) => x.facilityId === facId && (x.vendorId === 0 || x.vendorId === vendorId)
      );

      if (approversForFacility?.length > 0) {
        setSelectedApprovers((prev) => {
          const existingApprovers = new Set(prev.map((approver) => approver.approverEmail));

          return [
            ...prev,
            ...approversForFacility
              .filter((a) => !existingApprovers.has(a.approverEmail))
              .map((a) => ({
                approvalIndex: a.approvalIndex,
                approverEmail: a.approverEmail,
                approverFullName: a.approverFullName,
                status: 1,
                id: a.id,
              })),
          ];
        });
      }
    }, [vendorId, facId]);

    const handleInvoiceFormSave = async (data) => {
      withDuplicateCheck(async () => {
        if (editInvoice) {
          // Save default approvers if exist
          if (selectedApprovers && selectedApprovers.length > 0) {
            await dispatch(saveApprovers(id, selectedApprovers));
          }
          const response = await dispatch(invoiceForm({ invoice: data }));
          if (response.success) {
            setUnsavedData(null);
            setEditInvoice(false);
            if (isFacilityFieldEdited) getInvoiceViewers(invoice.id);
            await saveUnsavedGls();
          }
          if (response.message) {
            if (response.message.includes('already assigned to a different invoice')) {
              const match = response.message.match(/\[Duplicate ID: (\d+)]/);
              const duplicateInvoiceId = match ? match[1] : null;
              const messageWithoutDuplicateId = response.message.replace(/\[Duplicate ID: \d+\]/, '');

              enqueueSnackbar(
                <span>
                  <Link
                    color="#fff"
                    sx={{ textDecoration: 'underline' }}
                    onClick={() => {
                      if (duplicateInvoiceId) {
                        const newUrl = `${window.location.origin}/dashboard/invoicing/previous/${duplicateInvoiceId}`;
                        window.open(newUrl, '_blank');
                      }
                    }}
                  >
                    {messageWithoutDuplicateId}
                  </Link>
                </span>,
                {
                  variant: response.success ? 'success' : 'error',
                  persist: response.keepFormOpen,
                }
              );
            } else {
              enqueueSnackbar(response.message, {
                variant: response.success ? 'success' : 'error',
                persist: response.keepFormOpen,
              });
            }
          }
          return response?.success ?? false;
        }
        // If not editInvoice, return false by default
        return false;
      }, 'Save');
    };

    function isFacilityFieldEdited() {
      const newVal = getValues()?.facilityId;
      const origVal = invoiceItemData?.invoice?.facilityId;
      return newVal !== origVal;
    }

    function isInvoiceEdited() {
      const keysToCheck = [
        'facilityId',
        'vendorId',
        'vendorInvoiceId',
        'invoiceId',
        'invoiceType',
        'referenceNumber',
        'date',
        'dueDate',
        'glPostingDate',
        'total',
        'shipping',
      ];

      return !keysToCheck.every((key) => {
        const value1 = getValues()?.[key];
        const value2 = invoiceItemData?.invoice[key];

        if (key === 'invoiceType' && value1 === 'Invoice' && value2 === null) return true;
        if (value1 instanceof Date) return fMonthDayYearShort(value1) === fMonthDayYearShort(value2);
        if (!Number.isNaN(+value2)) return Number(value1) === Number(value2);
        if (typeof value1 === 'object' && typeof value2 === 'object' && value1 !== null && value2 !== null)
          return JSON.stringify(value1) === JSON.stringify(value2);
        return (value1 === null && value2 === '') || (value1 === '' && value2 === null) || value1 === value2;
      });
    }

    const onChangeField = async (name, newValue) => {
      setValue(name, newValue);
      setUnsavedData((prev) => ({
        ...prev,
        [name]: newValue,
      }));
      if (!(parsedFields & InvoiceParsedField.dueDate) && invoice?.sourceMethod === 'Manual') {
        if (name === 'date' && newValue) {
          const invoiceDate = parseDateOnlyToLocalDate(newValue);
          if (invoiceDate && isValid(invoiceDate)) {
            const dueDateStr = format(addDays(invoiceDate, 30), 'yyyy-MM-dd');
            setValue('dueDate', dueDateStr);
            setUnsavedData((prev) => ({
              ...prev,
              dueDate: dueDateStr,
            }));
          }
        }
      }
    };
    const handleVendorTotalChange = async (newValue) => {
      if (!Number.isNaN(+newValue)) {
        if (Number(newValue) < 0) {
          setValue('invoiceType', 'CreditMemo');
        } else if (Number(newValue) > 0) {
          setValue('invoiceType', 'Invoice');
        }
        setValue('total', newValue);
      }
      setUnsavedData((prev) => ({
        ...prev,
        total: newValue,
      }));
    };
    const handleInvoiceTypeChange = async (e) => {
      if (invoice.sourceMethod === 'Manual') {
        if (e.target.value === 'Invoice') {
          setValue('total', `${Math.abs(getValues().total)}`);
        } else {
          setValue('total', `-${Math.abs(getValues().total)}`);
        }
      }

      setValue('invoiceType', e.target.value);
      setUnsavedData((prev) => ({
        ...prev,
        invoiceType: e.target.value,
      }));
    };

    const creditMemoMismatch = useMemo(
      () => invoice?.invoiceType === 'CreditMemo' && calculatedTotal >= 0,
      [invoice?.invoiceType, calculatedTotal]
    );
    const invoiceTypeMismatch = useMemo(
      () => invoice?.invoiceType !== 'CreditMemo' && calculatedTotal < 0,
      [invoice?.invoiceType, calculatedTotal]
    );

    useEffect(() => {
      if (manualInvoice && bookingTypeId !== invoice?.bookingTypeId && selectedVendor?.id !== 0) {
        const similarVendor = vendorOptions.find(
          (v) =>
            (v.label?.toLowerCase() === selectedVendor?.name?.toLowerCase() ||
              v.bookingCode === selectedVendor?.bookingCode) &&
            v.bookingTypeId === bookingTypeId
        );
        if (similarVendor) updateVendorValue(similarVendor);
      }
      setInvoiceFacilityId(facId);
    }, [facId, vendorOptions]);

    useEffect(() => {
      initializeData();
    }, [invoiceItemData?.invoice, dataLoaded]);

    //update disable/enable invoice save button
    useEffect(() => {
      if (dataLoaded) {
        if (unsavedData != null) {
          setEditInvoice(isInvoiceEdited());
        }
      }
    }, [dataLoaded, invoice, unsavedData]);

    useEffect(() => {
      async function fetchData() {
        if (date && invoiceItemData?.invoice?.glPostingDate === null) {
          await dispatch(changeInvoiceField(id, { key: 'GlPostingDate', value: invoiceItemData?.invoice?.date }));
        }
        if (invoice?.invoiceType === null)
          dispatch(
            changeInvoiceField(id, { key: 'InvoiceType', value: invoice?.total >= 0 ? 'Invoice' : 'CreditMemo' })
          );
      }
      if (hasEditAccess) fetchData();
    }, [invoice]);

    const highlightItemFieldName = useMemo(() => {
      if (highlightItem?.form !== 'invoiceinfo') return null;
      const str = highlightItem?.description;
      const match = str.match(/'([^']+)'/);
      const result = match ? match[1] : null;
      return result;
    }, [highlightItem]);

    return (
      <>
        <Box sx={{}}>
          <Box sx={{ ...mainBoxStyle }}>
            <form onSubmit={handleSubmit(handleInvoiceFormSave)}>
              <Box sx={headerStyle}>
                <Typography color="info.main" sx={{ fontSize: '13px', fontWeight: 'bold', alignContent: 'center' }}>
                  Invoice Information
                </Typography>
                <Tooltip title={`${expand ? 'more' : 'less'}`}>
                  <IconButton
                    onClick={() => setExpand(!expand)}
                    sx={{ color: 'info.main', padding: 0, height: 'fit-content', mt: 0.5 }}
                  >
                    <Iconify icon={`ic:twotone-expand-${expand ? 'more' : 'less'}`} width={26} height={26} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Scrollbar sx={{ padding: '0 10px 10px 10px', maxHeight: expand ? '150px' : '' }}>
                <Box>
                  <FieldMappingProvider
                    formType="invoice"
                    parsedFields={parsedFields}
                    onBobAIHoverStart={onBobAIHoverStart}
                    onBobAIHoverEnd={onBobAIHoverEnd}
                  >
                    <Stack sx={formSectionStyle}>
                      {!getValues()?.multiFacility ? (
                        <>
                          <Controller
                            key={!hasEditAccess ? 'facility' : 'facilityId'}
                            name={!hasEditAccess ? 'facility' : 'facilityId'}                            control={control}
                            options={facilities || []}
                            render={({ field }) => (
                              <FieldMapping
                                fieldName="Facility * "
                                  name={!hasEditAccess ? 'facility' : 'facilityId'}
                                fieldType={!hasEditAccess ? 'text' : 'autocomplete'}
                                onChange={(event, newValue) => {
                                  setValue('facilityId', newValue.value);
                                  setValue('facility', newValue.label);
                                  setUnsavedData((prev) => ({
                                    ...prev,
                                    facility: newValue.label,
                                  }));
                                }}
                                loading={!invoiceDataLoaded}
                                disabled={!hasEditAccess}
                                options={facilities || []}
                                control={control}
                                sx={{
                                  backgroundColor:
                                    highlightItemFieldName?.toLowerCase() === 'facility' ? '#d8ebf9' : 'transparent',
                                }}
                              />
                            )}
                            rules={{ required: true }}
                          />
                        </>
                      ) : (
                        <Accordion>
                          <AccordionSummary
                            expandIcon={
                              <Icon
                                icon="material-symbols-light:expand-more-rounded"
                                color="#8dc63f"
                                height="20px"
                                width="20px"
                              />
                            }
                            sx={{
                              minHeight: 0,
                              p: 0,
                              '& .MuiAccordionSummary-content': { my: 0 },
                              '& .MuiButtonBase-root-MuiAccordionSummary-root': {
                                minHeight: 0,
                              },
                            }}
                          >
                            <Typography sx={fieldStyle}>Multiple facilities</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            {invoiceGLItems?.length === 0 ? (
                              <>
                                <Tooltip>{facilities?.find((x) => x.value === glRows?.[0]?.facilityId)?.label}</Tooltip>
                              </>
                            ) : (
                              <Scrollbar sx={{ maxHeight: '250px' }}>
                                <List sx={{ listStyle: 'decimal', pl: 4 }}>
                                  {getValues().invoiceFacilities.map((facility, index) => {
                                    const glFacilities = glRows?.filter((x) => x.facilityId === facility.facId);
                                    return (
                                      <>
                                        <ListItem sx={{ display: 'list-item', cursor: 'pointer' }} key={index}>
                                          {facility.facilityName} &nbsp;
                                          <Tooltip
                                            onClick={() => {
                                              setOpenGlCodeSummary(true);
                                              const glIds = [];
                                              glFacilities?.forEach((item) => {
                                                glIds.push(item.id);
                                              });
                                              setHighlightedGls(glIds);
                                            }}
                                            title={glFacilities?.map((item, index) =>
                                              index > 0 ? `, ${item.glCode}` : item.glCode
                                            )}
                                          >
                                            <Icon icon="material-symbols:info-outline" color="#0075db" />
                                          </Tooltip>
                                        </ListItem>
                                      </>
                                    );
                                  })}
                                </List>
                              </Scrollbar>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      )}
                      <Box>
                        <Controller
                          name={'vendorId'}
                          control={control}
                          render={({ field }) => (
                            <FieldMapping
                              sx={{
                                backgroundColor:
                                  highlightItemFieldName?.toLowerCase() === 'vendor' ? '#d8ebf9' : 'transparent',
                              }}
                              fieldName="Vendor * "
                              name="vendorId"
                              fieldType={'autocomplete'}
                              tooltipText={vendorTooltipText()}
                              filterOptions={(options, { inputValue }) => {
                                const search = inputValue?.toLowerCase() || "";
                                return options.filter(
                                  (option) =>
                                    option.label?.toLowerCase()?.includes(search) ||
                                    option.address?.toLowerCase()?.includes(search) ||
                                    option.secondAddressLine?.toLowerCase()?.includes(search) ||
                                    option.bookingCode?.includes(inputValue)
                                );
                              }}
                              onChange={(event, newValue) => {
                                updateVendorValue(newValue);
                              }}
                              loading={!invoiceDataLoaded}
                              options={vendorOptions}
                              PaperComponent={(props) => (
                                <Paper
                                  {...props}
                                  sx={{
                                    fontSize: '12px',
                                    '& .MuiAutocomplete-listbox': {
                                      paddingTop: 0,
                                    },
                                  }}
                                >
                                  <Tooltip
                                    title={
                                      canAddVendor
                                        ? 'Add a new vendor'
                                        : "You don't have permission to add vendors"
                                    }
                                    arrow
                                  >
                                    <Box
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        if (canAddVendor) {
                                          navigate('/dashboard/invoicing/vendors');
                                        }
                                      }}
                                      sx={{
                                        px: 2,
                                        py: 0.75,
                                        fontSize: '14px',
                                        lineHeight: 1.2,
                                        textDecoration: 'underline',
                                        color: canAddVendor ? '#1976d2' : '#9e9e9e',
                                        cursor: canAddVendor ? 'pointer' : 'not-allowed',
                                        borderBottom: '1px solid #e0e0e0',
                                        position: 'sticky',
                                        top: 0,
                                        backgroundColor: 'white',
                                        zIndex: 1,
                                          '& *': {
                                          fontSize: '14px !important',
                                          lineHeight: '16px',
                                        },
                                      }}
                                    >
                                      Add Vendor
                                    </Box>
                                  </Tooltip>
                                  {props.children}
                                </Paper>
                              )}
                              renderOption={(props, option) =>                                
                                  manualInvoice ? (
                                  <li {...props} id={option.value}>
                                    <Box
                                      sx={{
                                        textAlign: 'left',
                                        display: 'flex',
                                        justifyContent: 'start',
                                        flexDirection: 'column',
                                      }}
                                    >
                                      <span>{option.label}</span>

                                      {option.address && (
                                        <span style={{ fontSize: '0.8rem', color: '#777' }}>{option.address}</span>
                                      )}

                                      {option.secondAddressLine && (
                                        <span style={{ fontSize: '0.8rem', color: '#777' }}>
                                          {option.secondAddressLine}
                                        </span>
                                      )}

                                      {option.bookingCode && (
                                        <span style={{ fontSize: '0.75rem', color: '#777' }}>
                                          Vendor code:&nbsp;{option.bookingCode}
                                        </span>
                                      )}
                                    </Box>
                                  </li>
                                ) : (
                                  <li {...props}>{option.label}</li>
                                )
                              }
                              disabled={!hasEditAccess}
                              control={control}
                            />
                          )}
                        />
                      </Box>
                    </Stack>
                    <Divider variant="fullWidth" sx={dividerStyle} />
                    <Stack sx={formSectionStyle}>
                      <Box
                        sx={{
                          backgroundColor: highlightItemFieldName?.toLowerCase() === 'po' ? '#d8ebf9' : 'transparent',
                        }}
                      >
                        <Stack spacing={1} direction={'row'} alignItems={'center'}>
                          <Typography
                            color={invoiceItemData?.invoice?.orderId ? 'secondary' : 'inherit'}
                            sx={{
                              fontSize: '14px',
                              cursor: invoiceItemData?.invoice?.orderId ? 'pointer' : '',
                              textDecoration: invoiceItemData?.invoice?.orderId ? 'underline' : '',
                            }}
                            onClick={() => {
                              if (invoiceItemData?.invoice?.orderId) openOrderInNewTab();
                            }}
                          >
                            PO#
                          </Typography>
                          {(parsedFields & InvoiceParsedField?.invoiceId) > 0 &&
                            bobAIIcon({
                              onMouseEnter: () => onBobAIHoverStart?.({ fieldName: 'invoiceId' }),
                              onMouseLeave: () => onBobAIHoverEnd?.(),
                            })}

                          <Controller
                            name="invoiceId"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                variant="standard"
                                onChange={(e) => {
                                  field.onChange();
                                  const newValue = e.target.value;
                                  onChangeField('invoiceId', newValue);
                                }}
                                sx={{ flexGrow: 1 }}
                                value={field.value || ''}
                                disabled={!hasEditAccess}
                                inputRef={field.ref}
                                size="small"
                                InputProps={{ sx: { fontSize: '12px', mt: '5px' } }}
                                fullWidth
                                autoComplete="off"
                              />
                            )}
                          />
                        </Stack>
                      </Box>

                      <Controller
                        sx={{ ml: 1 }}
                        name={'vendorInvoiceId'}
                        control={control}
                        render={({ field }) => (
                          <FieldMapping
                            fieldName="Invoice #*"
                            name="vendorInvoiceId"
                            sx={{
                              backgroundColor:
                                highlightItemFieldName?.toLowerCase() === 'invoice' ? '#d8ebf9' : 'transparent',
                            }}
                            fieldType={'text'}
                            onChange={(e) => {
                              const name = e?.target.name;
                              const newValue = e.target.value;
                              onChangeField(name, newValue);
                            }}
                            disabled={!hasEditAccess}
                            control={control}
                          />
                        )}
                        rules={{ required: true }}
                      />
                      <Controller
                        name={'invoiceType'}
                        control={control}
                        render={({ field }) => (
                          <FieldMapping
                            sx={{
                              backgroundColor:
                                highlightItemFieldName?.toLowerCase() === 'invoice type' ? '#d8ebf9' : 'transparent',
                            }}
                            fieldName="Invoice type * "
                            name="invoiceType"
                            fieldType={'select'}
                            onChange={handleInvoiceTypeChange}
                            options={[
                              { label: 'Invoice', value: 'Invoice' },
                              { label: 'Credit Memo', value: 'CreditMemo' },
                            ]}
                            disabled={!hasEditAccess}
                            control={control}
                          />
                        )}
                        rules={{ required: true }}
                      />
                      <Controller
                        name={'referenceNumber'}
                        control={control}
                        render={({ field }) => (
                          <FieldMapping
                            sx={{
                              backgroundColor:
                                highlightItemFieldName?.toLowerCase() === 'reference #' ? '#d8ebf9' : 'transparent',
                            }}
                            fieldName="Reference #"
                            value={getValues()?.referenceNumber}
                            name="referenceNumber"
                            fieldType={'text'}
                            control={control}
                            onChange={(e) => {
                              const name = e?.target.name;
                              const newValue = e.target.value;
                              onChangeField(name, newValue);
                            }}
                            disabled={!hasEditAccess}
                          />
                        )}
                      />
                    </Stack>
                    <Divider variant="fullWidth" sx={dividerStyle} />
                    <Stack sx={formSectionStyle}>
                      <Controller
                        name={'date'}
                        control={control}
                        render={({ field }) => (
                          <FieldMapping
                            sx={{
                              backgroundColor:
                                highlightItemFieldName?.toLowerCase() === 'invoice date' ? '#d8ebf9' : 'transparent',
                            }}
                            fieldName="Invoice date * "
                            //value={invoiceValues?.date}
                            name="date"
                            fieldType={'date'}
                            onChange={(newDate) => {
                              onChangeField('date', newDate);
                            }}
                            control={control}
                            disabled={!hasEditAccess}
                          />
                        )}
                        rules={{ required: true }}
                      />
                      <Controller
                        name={'dueDate'}
                        control={control}
                        render={({ field }) => (
                          <FieldMapping
                            sx={{
                              backgroundColor:
                                highlightItemFieldName?.toLowerCase() === 'due date' ? '#d8ebf9' : 'transparent',
                            }}
                            fieldName="Due date"
                            //value={invoiceValues?.dueDate}
                            name="dueDate"
                            fieldType={'date'}
                            onChange={(newDate) => {
                              onChangeField('dueDate', newDate);
                            }}
                            control={control}
                            disabled={!hasEditAccess}
                          />
                        )}
                      />
                      <Controller
                        name={'glPostingDate'}
                        control={control}
                        render={({ field }) => (
                          <FieldMapping
                            sx={{
                              backgroundColor:
                                highlightItemFieldName?.toLowerCase() === 'gl posting date' ? '#d8ebf9' : 'transparent',
                            }}
                            fieldName="Gl Posting Date"
                            name="glPostingDate"
                            fieldType={'date'}
                            onChange={(newDate) => {
                              onChangeField('glPostingDate', newDate);
                            }}
                            control={control}
                            disabled={!hasEditAccess}
                          />
                        )}
                      />
                    </Stack>
                    <Divider variant="fullWidth" sx={dividerStyle} />
                    <Stack sx={formSectionStyle}>
                      {invoice?.sourceMethod === 'Manual' ? (
                        <Stack direction={'row'} alignItems={'center'} sx={{ mt: 0 }}>
                          <Controller
                            name={'total'}
                            control={control}
                            render={({ field }) => (
                              <FieldMapping
                                sx={{
                                  backgroundColor:
                                    highlightItemFieldName?.toLowerCase() === 'total' ? '#d8ebf9' : 'transparent',
                                }}
                                fieldName="Invoice total"
                                name="total"
                                fieldType={'currencyCalc'}
                                onChange={handleVendorTotalChange}
                                control={control}
                                disabled={!hasEditAccess}
                              />
                            )}
                          />
                          <Tooltip
                            title={
                              fCurrency(getValues()?.total) === fCurrency(calculatedTotal)
                                ? 'Total matches the  GLs total.'
                                : `Total doesn't match the GLs total: ${fCurrency(calculatedTotal)} `
                            }
                            arrow
                          >
                            {fCurrency(getValues()?.total) === fCurrency(calculatedTotal) ? (
                              <Typography display={'flex'} color="secondary">
                                <CheckCircleOutlineIcon fontSize={'1rem'} />
                              </Typography>
                            ) : (
                              <Typography display={'flex'} color="error.main">
                                <ErrorOutlineIcon fontSize={'1rem'} />
                              </Typography>
                            )}
                          </Tooltip>
                        </Stack>
                      ) : (
                        <>
                          <Stack spacing={1} direction={'row'} alignItems={'center'}>
                            <Typography sx={fieldStyle} variant="body2" color={'#060606'}>
                              Invoice total:
                            </Typography>
                            <Typography sx={{ fontSize: '12px' }} color={'#060606'}>
                              {fCurrency(calculatedTotal)}
                            </Typography>
                            <Tooltip
                              title={
                                fCurrency(getValues()?.total) !== fCurrency(calculatedTotal)
                                  ? `Total doesn't match the vendor's total: ${fCurrency(
                                      invoiceItemData?.invoice?.total
                                    )}`
                                  : creditMemoMismatch
                                  ? `This invoice is marked as a "Credit Memo", but the total is positive: ${fCurrency(
                                      calculatedTotal
                                    )}. Credit Memos should have negative totals.`
                                  : invoiceTypeMismatch
                                  ? `This invoice is marked as "Invoice", but the total is negative:${fCurrency(
                                      calculatedTotal
                                    )}. Regular invoices should have positive totals - consider changing it to a "Credit Memo" if applicable.`
                                  : "Total matches the vendor's total."
                              }
                              arrow
                            >
                              {fCurrency(getValues()?.total) === fCurrency(calculatedTotal) &&
                              !creditMemoMismatch &&
                              !invoiceTypeMismatch ? (
                                <Typography display={'flex'} color="secondary">
                                  <CheckCircleOutlineIcon fontSize={'1rem'} />
                                </Typography>
                              ) : (
                                <Typography display={'flex'} color="error.main">
                                  <ErrorOutlineIcon fontSize={'1rem'} />
                                </Typography>
                              )}
                            </Tooltip>
                          </Stack>
                          <Stack spacing={1} direction={'row'} alignItems={'center'}>
                            <Typography sx={fieldStyle} variant="body2" color={'#060606'}>
                              Shipping total:
                            </Typography>
                            <Typography sx={{ fontSize: '12px' }} color={'#060606'}>
                              {fCurrency(calculatedShippingTotal)}
                            </Typography>
                            <Tooltip
                              title={
                                fCurrency(getValues()?.shipping) === fCurrency(calculatedShippingTotal)
                                  ? "Shipping matches the vendor's shipping total."
                                  : `Shipping doesn't match the vendor's shipping total: ${fCurrency(
                                      invoiceItemData?.invoice?.shipping
                                    )}`
                              }
                              arrow
                            >
                              {fCurrency(getValues()?.shipping) === fCurrency(calculatedShippingTotal) ? (
                                <Typography display={'flex'} color="secondary">
                                  <CheckCircleOutlineIcon fontSize={'1rem'} />
                                </Typography>
                              ) : (
                                <Typography display={'flex'} color="error.main">
                                  <ErrorOutlineIcon fontSize={'1rem'} />
                                </Typography>
                              )}
                            </Tooltip>
                          </Stack>
                        </>
                      )}
                    </Stack>
                  </FieldMappingProvider>
                </Box>
              </Scrollbar>
              <Box sx={{ justifyItems: 'stretch' }}>
                <Button
                  variant="outlined"
                  size="small"
                  color="secondary"
                  type="submit"
                  sx={{
                    display: editInvoice || (invoiceItemData === null && unsavedData != null) ? 'block' : 'none',
                    mx: 1,
                    mb: 1,
                  }}
                >
                  Save
                </Button>
              </Box>{' '}
            </form>
            {!invoiceItemData?.invoice?.sent && (
              <>
                {duplicateInvoiceId === Number(id) && duplicates.length > 0 ? (
                  <DuplicateInvoiceAlert />
                ) : (
                  relatedInvoiceId === Number(id) &&
                  related.length > 0 && (
                    <RelatedInvoiceAlert setHighlightedRelatedInvoices={setHighlightedRelatedInvoices} />
                  )
                )}
              </>
            )}
          </Box>
        </Box>
        <DuplicateInvoicePopup
          open={showDuplicatePopup}
          onClose={formPopupProps.handleClose}
          onSubmitAnyway={formPopupProps.handleSubmitAnyway}
          submitLabel={formPopupProps.pendingSubmitLabel}
        />
      </>
    );
  }
);
export default InvoiceForm;
const dividerStyle = {
  borderColor: 'info.main',
  height: '1px',
  my: 0.5,
  borderStyle: 'dashed',
};
const mainBoxStyle = {
  borderRadius: '4px',
  boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
  backgroundColor: '#fff',
  overflow: 'auto',
  border: 'solid 1px #c1c9d0',
  //maxHeight: '450px',
};
const fieldStyle = { color: '#060606', fontSize: '14px' };
const formSectionStyle = {
  padding: '5px',
};
const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  px: 2,
  pt: 1,
  borderColor: 'info.main',
};
